import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProps } from 'react-beautiful-dnd';
import { useGetIdentityOptionsQuery, useUpdateIdentityOptionsMutation } from '../../redux/adminApiSlice';

type OptionType = 'gender' | 'race' | 'sexualOrientation';

interface Option {
  id: string;
  value: string;
}

interface IdentityOptionsType {
  gender: Option[];
  race: Option[];
  sexualOrientation: Option[];
}

interface OptionEditDialogProps {
  open: boolean;
  onClose: () => void;
  type: OptionType;
  option: Option | null;
  onSave: (value: string) => void;
  isNew?: boolean;
}

const OptionEditDialog: React.FC<OptionEditDialogProps> = ({
  open,
  onClose,
  type,
  option,
  onSave,
  isNew = false
}) => {
  const [editValue, setEditValue] = useState(option?.value || '');

  const handleSave = () => {
    onSave(editValue);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isNew ? 'Add New' : 'Edit'} {type.charAt(0).toUpperCase() + type.slice(1)} Option</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          size="small"
          margin="dense"
          label="Value"
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button size="small" onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const IdentityOptions: React.FC = () => {
  const { data: options, isLoading } = useGetIdentityOptionsQuery() as { 
    data: IdentityOptionsType | undefined;
    isLoading: boolean;
  };
  const [updateIdentityOptions] = useUpdateIdentityOptionsMutation();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditType, setCurrentEditType] = useState<OptionType>('gender');
  const [currentEditOption, setCurrentEditOption] = useState<Option | null>(null);
  const [isNewOption, setIsNewOption] = useState(false);

  const createOptionId = (value: string) => value.toLowerCase().replace(/\s+/g, '-');

  const handleAddOption = (type: OptionType) => {
    setCurrentEditType(type);
    setCurrentEditOption(null);
    setIsNewOption(true);
    setEditDialogOpen(true);
  };

  const handleEditOption = (type: OptionType, option: Option) => {
    setCurrentEditType(type);
    setCurrentEditOption(option);
    setIsNewOption(false);
    setEditDialogOpen(true);
  };

  const handleDeleteOption = async (type: OptionType, optionToDelete: Option) => {
    if (!options) return;
    
    const updatedOptions = {
      ...options,
      [type]: options[type].filter((opt) => opt.id !== optionToDelete.id)
    };
    
    try {
      await updateIdentityOptions(updatedOptions).unwrap();
    } catch (error) {
      console.error('Failed to delete option:', error);
    }
  };

  const handleSaveOption = async (newValue: string) => {
    if (!options) return;
    
    const updatedOptions = {
      ...options,
      [currentEditType]: isNewOption
        ? [...options[currentEditType], { id: createOptionId(newValue), value: newValue }]
        : options[currentEditType].map((opt) =>
            opt.id === currentEditOption?.id ? { ...opt, value: newValue } : opt
          )
    };
    
    try {
      await updateIdentityOptions(updatedOptions).unwrap();
    } catch (error) {
      console.error('Failed to save option:', error);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!options || !result.destination) return;

    const { source, destination } = result;
    const type = source.droppableId as OptionType;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const items = Array.from(options[type]);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    const updatedOptions = {
      ...options,
      [type]: items
    };

    updateIdentityOptions(updatedOptions);
  };

  const renderOptionsList = (type: OptionType, title: string) => {
    if (!options) return null;
    
    return (
      <Paper sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>{title}</Typography>
          <IconButton size="small" onClick={() => handleAddOption(type)} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
        <StrictModeDroppable droppableId={type}>
          {(provided) => (
            <List 
              dense 
              disablePadding
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {options[type].map((option, index) => (
                <Draggable key={option.id} draggableId={`${type}-${option.id}`} index={index}>
                  {(provided, snapshot) => (
                    <ListItem
                      dense
                      disablePadding
                      sx={{ 
                        px: 1,
                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <Box 
                        {...provided.dragHandleProps} 
                        sx={{ 
                          mr: 1, 
                          display: 'flex', 
                          alignItems: 'center',
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' }
                        }}
                      >
                        <DragIndicator fontSize="small" sx={{ color: 'text.secondary' }} />
                      </Box>
                      <ListItemText 
                        primary={option.value} 
                        primaryTypographyProps={{ 
                          variant: 'body2'
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEditOption(type, option)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteOption(type, option)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </StrictModeDroppable>
      </Paper>
    );
  };

  if (isLoading) {
    return <Typography variant="body2">Loading...</Typography>;
  }

  if (!options) {
    return <Typography variant="body2">No identity options found.</Typography>;
  }

  return (
    <Box 
      sx={{ 
        height: 'calc(100vh - 64px)',
        overflow: 'auto',
        p: 2
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>Identity Options</Typography>
      <Box sx={{ maxWidth: 600, margin: '0 auto', pb: 8 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          {renderOptionsList('gender', 'Gender Options')}
          {renderOptionsList('race', 'Race Options')}
          {renderOptionsList('sexualOrientation', 'Sexual Orientation Options')}
        </DragDropContext>
      </Box>
      
      <OptionEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        type={currentEditType}
        option={currentEditOption}
        onSave={handleSaveOption}
        isNew={isNewOption}
      />
    </Box>
  );
};

export default IdentityOptions; 